import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { waLink } from "../lib/site";

const LINES = ["SOLLUS —", "Tecnologia e", "eficiência", "para o campo."];
const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative overflow-hidden bg-white lg:h-[calc(100svh-72px)] lg:max-h-[760px] lg:min-h-[560px]">
      <motion.div style={{ y }} className="relative h-[300px] w-full sm:h-[420px] lg:absolute lg:inset-0 lg:h-full">
        <motion.img src="/img/hero.jpg" alt="Implemento agrícola vermelho SOLLUS em lavoura"
          initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease }}
          className="h-full w-full object-cover object-[70%_center]" />
        <div className="hero-wash absolute inset-0 hidden lg:block" />
      </motion.div>

      <div className="container-x relative z-10 flex h-full items-center py-8 lg:py-0">
        <div className="max-w-2xl">
          <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            Máquinas e implementos agrícolas
          </motion.p>
          <h1 className="mt-4 font-display text-5xl font-bold uppercase leading-[0.92] text-ink sm:text-6xl lg:text-[76px]" data-testid="hero-title">
            {LINES.map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span className={`block ${i === 2 ? "text-brand" : ""}`}
                  initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.25 + i * 0.12, ease }}>
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p className="mt-5 max-w-md text-base text-steel md:text-lg" data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}>
            Máquinas e implementos agrícolas desenvolvidos para acompanhar a força do produtor rural.
          </motion.p>
          <motion.div className="mt-7 flex flex-col gap-3 sm:flex-row"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}>
            <Link to="/produtos" className="btn-dark justify-center whitespace-nowrap" data-testid="hero-cta-produtos">Conheça nossos produtos <ArrowRight className="h-4 w-4" /></Link>
            <a href={waLink()} target="_blank" rel="noreferrer" className="btn-red justify-center whitespace-nowrap" data-testid="hero-cta-orcamento">
              <MessageCircle className="h-4 w-4" /> Solicitar orçamento agora
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8, ease }}
        className="absolute bottom-8 right-8 z-10 hidden w-64 rounded-lg border border-white/60 bg-white/80 p-5 shadow-2xl backdrop-blur-xl xl:block" data-testid="hero-floating-card">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand">Atendimento direto</p>
        <p className="mt-2 font-display text-2xl font-bold uppercase leading-none text-ink">Orçamento em minutos pelo WhatsApp</p>
        <div className="mt-4 h-1 w-12 bg-brand" />
      </motion.div>
    </section>
  );
};
